// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { unwrap } from "@granite/api-client";
import { Button, TextField } from "@granite/ui";
import { useMutation } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useState, type SubmitEvent } from "react";

import { AuthLayout, FormAlert, linkClass } from "../components/AuthLayout.js";
import { errorMessage } from "../lib/errors.js";
import { t } from "../lib/i18n.js";
import { authClient } from "../lib/session.js";

const route = getRouteApi("/check-email");

/** After sign-up, and for asking for a new confirmation link. */
export function CheckEmailPage() {
  const search = route.useSearch();
  const [email, setEmail] = useState(search.email ?? "");
  const mutation = useMutation({
    mutationFn: async () => {
      unwrap(await authClient.POST("/v1/auth/verify-email/resend", { body: { email: email.trim() } }));
    },
  });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <AuthLayout
      title={t("checkEmail.title")}
      footer={
        <Link to="/login" className={linkClass}>
          {t("common.backToSignIn")}
        </Link>
      }
    >
      {mutation.isSuccess && <FormAlert tone="success">{t("checkEmail.resent")}</FormAlert>}
      {mutation.error && <FormAlert tone="error">{errorMessage(mutation.error)}</FormAlert>}
      <p className="mb-4 text-sm text-fg">
        {search.email ? t("checkEmail.sentTo", { email: search.email }) : t("checkEmail.generic")}
      </p>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {!search.email && (
          <TextField
            label={t("common.email")}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        )}
        <Button type="submit" variant="secondary" loading={mutation.isPending}>
          {t("checkEmail.resend")}
        </Button>
      </form>
    </AuthLayout>
  );
}
